import { useTranslation } from 'react-i18next';
import { Manual } from '../../../interfaces/customer-api';
import React, { useEffect, useMemo, useState } from 'react';
import css from './Manuals.module.scss';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { useOvermindState } from '../../state';
import { getLanguageName, getDefaultLanguageCode } from '../../languages';
import { ManualItem } from './ManualItem';

interface ManualsProps {
    manuals: Manual[];
}
interface ManualLanguage {
    code: string;
    name: string | undefined;
    manualsCount: number;
}
interface ManualsPerLanguage {
    [key: string]: {
        manualCount: number;
        code: string;
        name: string | undefined;
        manuals: Manual[];
    };
}

const OTHER_LANGUAGE_CODE = 'Other';

export const Manuals: React.FC<ManualsProps> = ({ manuals }: ManualsProps) => {
    const { t } = useTranslation('DocumentationTab');
    const { language } = useOvermindState();
    const defaultLanguageCode = getDefaultLanguageCode();
    const [selectedLanguage, setSelectedLanguage] = useState({
        code: language as string,
        name: getLanguageName(language as string),
    });

    const manualsPerLanguage: ManualsPerLanguage = useMemo(() => {
        const languageToManuals = manuals.reduce((acc, manual) => {
            const languageCode = manual.ietf_language_code === null ? OTHER_LANGUAGE_CODE : manual.ietf_language_code;
            acc[languageCode] = acc[languageCode] || {
                manualCount: 0,
                code: languageCode,
                name: getLanguageName(languageCode),
                manuals: [],
            };
            acc[languageCode].manualCount++;
            acc[languageCode].manuals.push(manual);
            return acc;
        }, {} as ManualsPerLanguage);

        // sort the manuals by type
        Object.entries(languageToManuals).forEach(([, data]) => {
            data.manuals = data.manuals.sort((manualA, manualB) => manualA.type.localeCompare(manualB.type));
        });

        return languageToManuals;
    }, [manuals]);

    const manualLanguages: ManualLanguage[] = useMemo(() => {
        const languageCodes = Array.from(new Set(manuals.map((manual) => manual.ietf_language_code)));
        return (
            languageCodes
                .map((code) => {
                    const languageCode = code === null ? OTHER_LANGUAGE_CODE : code;
                    return {
                        code: languageCode,
                        name: getLanguageName(languageCode),
                        manualsCount: manualsPerLanguage[languageCode].manualCount || 0,
                    };
                })
                // Sort manuals, but leave "Other" at the end
                .sort((languageA, languageB) => {
                    if (languageA.code === OTHER_LANGUAGE_CODE) return 1;
                    if (languageB.code === OTHER_LANGUAGE_CODE) return -1;
                    return languageA.name?.localeCompare(languageB.name || '') || 0;
                })
        );
    }, [manuals, manualsPerLanguage]);

    const maxManualsLanguage: ManualLanguage = useMemo(() => {
        const sorted = [...manualLanguages].sort(
            (languageA, languageB) => languageB.manualsCount - languageA.manualsCount,
        );
        return {
            code: sorted[0].code,
            name: sorted[0].name,
            manualsCount: sorted[0].manualsCount,
        };
    }, [manualLanguages]);

    useEffect(() => {
        // Use selected language if available,
        // otherwise default language,
        // otherwise first available language
        const getLanguageCode = (): string => {
            if (manualsPerLanguage[language]) {
                return language as string;
            }
            if (manualsPerLanguage[defaultLanguageCode]) {
                return defaultLanguageCode;
            }
            return Object.keys(manualsPerLanguage)[0];
        };

        const languageCode = getLanguageCode();
        setSelectedLanguage({
            code: languageCode,
            name: getLanguageName(languageCode),
        });
    }, [defaultLanguageCode, language, manualsPerLanguage]);

    const renderMoreManuals = () => {
        let manuals = manualsPerLanguage[defaultLanguageCode].manuals;
        if (maxManualsLanguage && maxManualsLanguage.code !== defaultLanguageCode) {
            manuals = manuals.concat(manualsPerLanguage[maxManualsLanguage.code].manuals);
        }

        const omitManualTypes = manualsPerLanguage[selectedLanguage.code].manuals.map((manual) => manual.type);
        manuals = manuals.filter((manual) => !omitManualTypes.includes(manual.type));

        if (manuals.length > 0) {
            return (
                <div>
                    <div className={css.label}>{t('See more manuals in other languages')}</div>
                    {manuals.map((manual) => (
                        <ManualItem key={`${manual.file_name}-${manual.type}`} manual={manual} />
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <div className={css.manualWidget} id="manuals-list">
            <h3>{t('Manuals')}</h3>
            <div className={css.manualLanguageSelector}>
                <InputLabel id="language-select-label" className={css.dropDownMenuLabel}>
                    {t('Select language')}
                </InputLabel>
                <FormControl>
                    <Select
                        labelId="language-select-label"
                        id="language-select"
                        value={selectedLanguage.code}
                        onChange={(event: SelectChangeEvent) => {
                            setSelectedLanguage({
                                code: event.target.value,
                                name: getLanguageName(event.target.value),
                            });
                        }}
                        className={css.dropDownMenu}
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    maxHeight: 500, // Adjust height as needed
                                    overflowY: 'auto',
                                },
                            },
                        }}
                    >
                        {manualLanguages.map((manualLanguage) => (
                            <MenuItem
                                sx={{
                                    fontSize: '12px',
                                    whiteSpace: 'normal',
                                }}
                                key={manualLanguage.name}
                                value={manualLanguage.code}
                                id={manualLanguage.code + '-language-option'}
                            >
                                {`${manualLanguage.name} (${manualLanguage.manualsCount} ${
                                    manualLanguage.manualsCount && manualLanguage.manualsCount > 1
                                        ? t('Manuals').toLowerCase()
                                        : t('Manual').toLowerCase()
                                })`}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
            {manualsPerLanguage[selectedLanguage.code] &&
                manualsPerLanguage[selectedLanguage.code].manuals.map((manual) => {
                    return <ManualItem key={`${manual.file_name}-${manual.type}`} manual={manual} />;
                })}
            {manualsPerLanguage[selectedLanguage.code] &&
                maxManualsLanguage &&
                manualsPerLanguage[selectedLanguage.code].manuals.length < maxManualsLanguage.manualsCount && (
                    <div>{renderMoreManuals()}</div>
                )}
        </div>
    );
};
